export type { ParsedTransactionRow } from './transaction-types';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { detectCurrencyFromPdfPages, type CurrencyInfo } from './currency';
import type { ParsedTransactionRow } from './transaction-types';

export interface ConversionResult {
  blob: Blob;
  rows: ParsedTransactionRow[];
  summary: {
    totalTransactions: number;
    duplicateCount: number;
    categories: Record<string, number>;
  };
  currency: CurrencyInfo;
}

export async function convertBankPdfToXlsx(file: File, enrich = true): Promise<ConversionResult> {
  const [
    pdfjsLib,
    XLSX,
    { extractTransactions },
    { categorize },
    { normalizeMerchant },
    { flagDuplicates },
  ] = await Promise.all([
    import('pdfjs-dist'),
    import('xlsx'),
    import('@/lib/pdf-parser'),
    import('@/lib/categorization'),
    import('@/lib/merchant-normalization'),
    import('@/lib/deduplication'),
  ]);

  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

  const pdf = await pdfjsLib.getDocument({ data: await file.arrayBuffer() }).promise;
  const pages: Array<{ items: unknown[] }> = [];

  for (let p = 1; p <= pdf.numPages; p++) {
    const page = await pdf.getPage(p);
    const content = await page.getTextContent();
    pages.push({ items: content.items });
  }

  const currency = detectCurrencyFromPdfPages(pages);
  const { transactions: rawRows } = extractTransactions(pages);
  const rows: ParsedTransactionRow[] = enrich
    ? flagDuplicates(
        rawRows.map((row) => ({
          ...row,
          merchant: normalizeMerchant(row.description ?? row.raw ?? ''),
          category: categorize(row.description ?? row.raw ?? ''),
        })),
      )
    : rawRows.map((row) => ({ ...row, isDuplicate: false }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, createTransactionsSheet(XLSX, rows, enrich, currency), 'Transactions');
  XLSX.utils.book_append_sheet(workbook, createRawDataSheet(XLSX, rows), 'Raw Data');

  const xlsxBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const blob = new Blob([xlsxBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  return {
    blob,
    rows,
    summary: buildSummary(rows),
    currency,
  };
}

function buildSummary(rows: ParsedTransactionRow[]) {
  const categories: Record<string, number> = {};
  let duplicateCount = 0;

  for (const row of rows) {
    if (row.isDuplicate) duplicateCount++;
    if (row.category) categories[row.category] = (categories[row.category] ?? 0) + 1;
  }

  return { totalTransactions: rows.length, duplicateCount, categories };
}

function createTransactionsSheet(
  XLSX: typeof import('xlsx'),
  transactions: ParsedTransactionRow[],
  enriched = false,
  currency: CurrencyInfo,
) {
  const headers = enriched
    ? ['Date', 'Merchant', 'Description', 'Category', 'Debit', 'Credit', 'Balance', 'Duplicate']
    : ['Date', 'Description', 'Debit', 'Credit', 'Balance'];

  const data: Array<Array<string | number>> = [headers];
  const amountColumnStart = enriched ? 4 : 2;
  const amountColumnEnd = enriched ? 6 : 4;

  for (const t of transactions) {
    if (enriched) {
      data.push([
        t.date ?? '',
        t.merchant ?? '',
        t.description ?? t.raw ?? '',
        t.category ?? '',
        t.debit ?? '',
        t.credit ?? '',
        t.balance ?? '',
        t.isDuplicate ? 'Yes' : '',
      ]);
    } else {
      data.push([
        t.date ?? '',
        t.description ?? t.raw ?? '',
        t.debit ?? '',
        t.credit ?? '',
        t.balance ?? '',
      ]);
    }
  }

  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const debitColumnIndex = enriched ? 4 : 2;
  const creditColumnIndex = enriched ? 5 : 3;

  for (let rowIndex = 1; rowIndex < data.length; rowIndex++) {
    for (let columnIndex = amountColumnStart; columnIndex <= amountColumnEnd; columnIndex++) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex });
      const cell = worksheet[address];
      if (cell && typeof cell.v === 'number') {
        cell.z =
          columnIndex === debitColumnIndex
            ? currency.debitNumberFormat
            : columnIndex === creditColumnIndex
              ? currency.creditNumberFormat
              : currency.numberFormat;
      }
    }
  }

  return worksheet;
}

function createRawDataSheet(XLSX: typeof import('xlsx'), transactions: ParsedTransactionRow[]) {
  const data: string[][] = [['Raw Data', 'Parsed Data'], ['', '']];

  for (const t of transactions) {
    data.push([
      t.raw ?? '',
      JSON.stringify({
        date: t.date,
        description: t.description,
        merchant: t.merchant,
        category: t.category,
        debit: t.debit,
        credit: t.credit,
        balance: t.balance,
        isDuplicate: t.isDuplicate,
      }),
    ]);
  }

  return XLSX.utils.aoa_to_sheet(data);
}
